import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { isStudentProfileComplete } from '../utils/student-profile-complete.util';

@Injectable({
  providedIn: 'root'
})
export class StudentAuthGuard implements CanActivate {
  
  // Rotas públicas que não precisam de autenticação completa
  private publicRoutes = [
    '/auth/login',
    '/register',
    '/student/onboarding' // Permitir acesso à página de onboarding
  ];

  // Rotas liberadas antes do onboarding (convite recebido logo após o cadastro)
  private incompleteProfileAllowedRoutes = [
    '/student/invite-detail'
  ];

  // Rotas que trainers também podem acessar
  private trainerAllowedRoutes = [
    '/student/student-profile' // Trainers podem ver perfis de alunos
  ];
  
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  /**
   * Verifica se o perfil do aluno está completo (nome válido + sexo).
   */
  private isProfileComplete(user: any): boolean {
    if (!user) return false;

    return isStudentProfileComplete(user);
  }

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const user = this.authService.getCurrentUser();
    const url = state.url;
    
    console.log('🔒 StudentAuthGuard - URL completa:', url, 'User:', user?.userType);
    
    // Permitir acesso às rotas públicas (login, verificação SMS e onboarding)
    if (this.publicRoutes.some(publicRoute => url.startsWith(publicRoute))) {
      console.log('✅ Rota pública, permitindo acesso');
      return true;
    }
    
    // Se não estiver autenticado, redireciona para login
    if (!user) {
      console.log('❌ Usuário não autenticado, redirecionando para login');
      return this.router.createUrlTree(['/auth/login'], { queryParams: { userType: 'aluno' } });
    }
    
    // Se for student, verificar se perfil está completo
    if (user.userType === 'student') {
      const profileComplete = this.isProfileComplete(user);
      
      // Se está tentando acessar onboarding mas perfil já está completo, redirecionar
      if (profileComplete && url.startsWith('/student/onboarding')) {
        console.log('✅ Perfil já completo, redirecionando para área principal');
        return this.router.createUrlTree(['/student']);
      }
      
      // Se perfil não está completo e não está tentando acessar onboarding, redirecionar
      const allowedWhileIncomplete = this.incompleteProfileAllowedRoutes.some(route => url.startsWith(route));
      if (!profileComplete && !url.startsWith('/student/onboarding') && !allowedWhileIncomplete) {
        console.log('❌ Perfil incompleto, redirecionando para onboarding');
        return this.router.createUrlTree(['/student/onboarding']);
      }
      
      console.log('✅ Usuário é student, permitindo acesso');
      return true;
    }
    
    // Se for trainer, verificar se está acessando uma rota permitida
    if (user.userType === 'trainer') {
      const isAllowedRoute = this.trainerAllowedRoutes.some(route => url.startsWith(route));
      if (isAllowedRoute) {
        console.log('✅ Trainer acessando rota permitida:', url);
        return true;
      }
      console.log('❌ Usuário é trainer, redirecionando para feed');
      return this.router.createUrlTree(['/trainer/feed']);
    }
    
    // Fallback: redireciona para seleção de tipo
    console.log('❌ Fallback, redirecionando para seleção de tipo');
    return this.router.createUrlTree(['/user-type-selection']);
  }
}

